import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  TableCell,
  TablePagination,
  TableRow,
  Typography,
} from '@mui/material';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import AdminPanelSettingsOutlinedIcon from '@mui/icons-material/AdminPanelSettingsOutlined';
import CheckCircleOutlinedIcon from '@mui/icons-material/CheckCircleOutlined';
import CloseOutlinedIcon from '@mui/icons-material/CloseOutlined';
import BlockOutlinedIcon from '@mui/icons-material/BlockOutlined';
import { AxiosError } from 'axios';
import { Form } from 'react-final-form';
import { useNavigate } from 'react-router';

import { changeUserRole, disableUser, getAllLocations, getAllUsers, updateUser } from '../../constants/api';
import { IApiErrorResponse, IApiLocation, IApiUser } from '../../constants/api-interfaces';
import { EditUserFormValues, EditUserRoleFormValues, UserRoleValue } from '../../constants/interfaces';
import { canAccessDashboardRoute } from '../../constants/utils';
import { editUserRoleValidation, editUserValidation } from '../../constants/validationSchemas';
import { useAuth } from '../../contexts/AuthContext';
import { useSnackbar } from '../../contexts/SnackbarContext';
import EditUserForm from '../../components/reusable/editUserForm';
import FormControlledSelect from '../../components/reusable/FormControlledSelect';
import StyledTable from '../../components/reusable/StyledTable';
const USERS_PER_PAGE = 20;

const emptyFormValues: EditUserFormValues = {
  firstName: '',
  lastName: '',
  email: '',
  phoneNumber: '',
  dateOfBirth: '',
  startDate: '',
  locationId: '',
};

const emptyRoleFormValues: EditUserRoleFormValues = {
  role: 'teammember',
};

const roleOptions: { label: string; value: UserRoleValue }[] = [
  { label: 'Team Member', value: 'teammember' },
  { label: 'Manager', value: 'manager' },
  { label: 'Admin', value: 'admin' },
  { label: 'Owner', value: 'owner' },
];

const formatDateForForm = (value?: string | null) => (value ? value.slice(0, 10) : '');

export default function UserManagement() {
  const navigate = useNavigate();
  const { auth } = useAuth();
  const { snackbarDispatch } = useSnackbar();
  const [users, setUsers] = useState<IApiUser[]>([]);
  const [locations, setLocations] = useState<IApiLocation[]>([]);
  const [selectedUser, setSelectedUser] = useState<IApiUser | null>(null);
  const [selectedRoleUser, setSelectedRoleUser] = useState<IApiUser | null>(null);
  const [selectedDisableUser, setSelectedDisableUser] = useState<IApiUser | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [page, setPage] = useState(0);

  const currentRole = auth?.user?.role ?? 'teammember';
  const canAccessPage = canAccessDashboardRoute(currentRole, 'user-management');

  useEffect(() => {
    if (!canAccessPage) {
      navigate('/dashboard', { replace: true });
    }
  }, [canAccessPage, navigate]);

  const fetchData = useCallback(async () => {
    setIsLoading(true);

    try {
      const [usersResponse, locationsResponse] = await Promise.all([getAllUsers(), getAllLocations()]);
      setUsers(usersResponse.data.users || []);
      setLocations(locationsResponse.data.locations || []);
    } catch (error) {
      const axiosError = error as AxiosError<IApiErrorResponse>;
      snackbarDispatch({
        type: 'setSnackbar',
        payload: {
          message: axiosError.response?.data?.message || 'Unable to load users and locations.',
          error: true,
        },
      });
    } finally {
      setIsLoading(false);
    }
  }, [snackbarDispatch]);

  useEffect(() => {
    if (!canAccessPage) {
      return;
    }

    fetchData();
  }, [canAccessPage, fetchData]);

  const initialFormValues = useMemo<EditUserFormValues>(() => {
    if (!selectedUser) {
      return emptyFormValues;
    }

    return {
      firstName: selectedUser.firstName || '',
      lastName: selectedUser.lastName || '',
      email: selectedUser.email || '',
      phoneNumber: selectedUser.phoneNumber || '',
      dateOfBirth: formatDateForForm(selectedUser.dateOfBirth),
      startDate: formatDateForForm(selectedUser.startDate),
      locationId: selectedUser.locationId || '',
    };
  }, [selectedUser]);

  const initialRoleFormValues = useMemo<EditUserRoleFormValues>(() => {
    if (!selectedRoleUser) {
      return emptyRoleFormValues;
    }

    return {
      role: selectedRoleUser.role as UserRoleValue,
    };
  }, [selectedRoleUser]);

  const paginatedUsers = useMemo(() => {
    const startIndex = page * USERS_PER_PAGE;
    return users.slice(startIndex, startIndex + USERS_PER_PAGE);
  }, [page, users]);

  useEffect(() => {
    const maxPage = Math.max(0, Math.ceil(users.length / USERS_PER_PAGE) - 1);

    if (page > maxPage) {
      setPage(maxPage);
    }
  }, [page, users.length]);

  const handleOpenUserEdit = (user: IApiUser) => {
    setSelectedUser(user);
  };

  const handleOpenRoleEdit = (user: IApiUser) => {
    setSelectedRoleUser(user);
  };

  const handleCloseUserDialog = () => {
    setSelectedUser(null);
  };

  const handleCloseRoleDialog = () => {
    setSelectedRoleUser(null);
  };

  const handleCloseDisableDialog = () => {
    setSelectedDisableUser(null);
  };

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleUserSubmit = async (values: EditUserFormValues) => {
    if (!selectedUser) {
      return;
    }

    setIsSubmitting(true);

    try {
      const updateResponse = await updateUser({
        id: selectedUser.id,
        firstName: values.firstName.trim() || undefined,
        lastName: values.lastName.trim() || undefined,
        phoneNumber: values.phoneNumber.trim() || undefined,
        dateOfBirth: values.dateOfBirth || undefined,
        startDate: values.startDate || undefined,
        locationId: values.locationId.trim() || undefined,
      });

      const refreshedUsersResponse = await getAllUsers();
      setUsers(refreshedUsersResponse.data.users || []);

      snackbarDispatch({
        type: 'setSnackbar',
        payload: {
          message: `${updateResponse.data.user.firstName || 'User'} has been updated successfully.`,
          error: false,
        },
      });

      handleCloseUserDialog();
    } catch (error) {
      const axiosError = error as AxiosError<IApiErrorResponse>;
      snackbarDispatch({
        type: 'setSnackbar',
        payload: {
          message: axiosError.response?.data?.message || 'Unable to update the user.',
          error: true,
        },
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleRoleSubmit = async (values: EditUserRoleFormValues) => {
    if (!selectedRoleUser) {
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await changeUserRole(selectedRoleUser.id, values.role);

      const refreshedUsersResponse = await getAllUsers();
      setUsers(refreshedUsersResponse.data.users || []);

      snackbarDispatch({
        type: 'setSnackbar',
        payload: {
          message: `${response.data.user.firstName || 'User'} role updated successfully.`,
          error: false,
        },
      });

      handleCloseRoleDialog();
    } catch (error) {
      const axiosError = error as AxiosError<IApiErrorResponse>;
      snackbarDispatch({
        type: 'setSnackbar',
        payload: {
          message: axiosError.response?.data?.message || 'Unable to update user role.',
          error: true,
        },
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDisableUser = async () => {
    if (!selectedDisableUser) {
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await disableUser(selectedDisableUser.id);

      const refreshedUsersResponse = await getAllUsers();
      setUsers(refreshedUsersResponse.data.users || []);

      snackbarDispatch({
        type: 'setSnackbar',
        payload: {
          message: `${response.data.user.firstName || 'User'} has been disabled successfully.`,
          error: false,
        },
      });

      handleCloseDisableDialog();
    } catch (error) {
      const axiosError = error as AxiosError<IApiErrorResponse>;
      snackbarDispatch({
        type: 'setSnackbar',
        payload: {
          message: axiosError.response?.data?.message || 'Unable to disable user.',
          error: true,
        },
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!canAccessPage) {
    return null;
  }

  return (
    <Box sx={{ px: { xs: 2, md: 4 }, py: 3 }}>
      <Typography variant="h4" sx={{ fontSize: '28px', fontWeight: 700, mb: 2 }}>
        User Management
      </Typography>

      <StyledTable
        columns={[
          { label: 'Employee #' },
          { label: 'Name' },
          { label: 'Email' },
          { label: 'Phone Number' },
          { label: 'Start Date' },
          { label: 'Role' },
          { label: 'Location' },
          { label: 'Enabled', align: 'center' },
          { label: 'Edit user', align: 'center' },
          { label: 'Edit Role', align: 'center' },
          { label: 'Disable user', align: 'center' },
        ]}
        hasRows={users.length > 0}
        isLoading={isLoading}
        loadingMessage="Loading users..."
        emptyMessage="No users found."
        pagination={
          <TablePagination
            component="div"
            count={users.length}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={USERS_PER_PAGE}
            rowsPerPageOptions={[USERS_PER_PAGE]}
          />
        }
      >
        {paginatedUsers.map((user) => {
          const fullName = [user.firstName, user.lastName].filter(Boolean).join(' ') || 'Unnamed user';

          return (
            <TableRow key={user.id} hover>
              <TableCell>{user.employeeNumber || '—'}</TableCell>
              <TableCell>{fullName}</TableCell>
              <TableCell>{user.email}</TableCell>
              <TableCell>{user.phoneNumber || '—'}</TableCell>
              <TableCell>{formatDateForForm(user.startDate) || '—'}</TableCell>
              <TableCell>{user.role}</TableCell>
              <TableCell>{user.location?.name || '—'}</TableCell>
              <TableCell align="center">
                {user.isDisabled ? (
                  <CloseOutlinedIcon sx={{ color: 'error.main' }} />
                ) : (
                  <CheckCircleOutlinedIcon sx={{ color: 'success.main' }} />
                )}
              </TableCell>
              <TableCell align="center">
                <IconButton
                  size="small"
                  color="primary"
                  aria-label="edit user"
                  onClick={() => handleOpenUserEdit(user)}
                  sx={{
                    border: '2px solid',
                    borderColor: 'primary.main',
                    p: 0.75,
                    '&:hover': { backgroundColor: 'rgba(25, 118, 210, 0.08)' },
                  }}
                >
                  <EditOutlinedIcon />
                </IconButton>
              </TableCell>
              <TableCell align="center">
                <IconButton
                  size="small"
                  color="primary"
                  aria-label="edit role"
                  onClick={() => handleOpenRoleEdit(user)}
                  sx={{
                    border: '2px solid',
                    borderColor: 'primary.main',
                    p: 0.75,
                    '&:hover': { backgroundColor: 'rgba(25, 118, 210, 0.08)' },
                  }}
                >
                  <AdminPanelSettingsOutlinedIcon />
                </IconButton>
              </TableCell>
              <TableCell align="center">
                <IconButton
                  aria-label="disable user"
                  onClick={() => setSelectedDisableUser(user)}
                  disabled={user.isDisabled}
                  sx={{
                    borderRadius: 1,
                    px: 1.25,
                    py: 0.5,
                    backgroundColor: user.isDisabled ? 'action.disabledBackground' : 'warning.main',
                    '&:hover': {
                      backgroundColor: user.isDisabled ? 'action.disabledBackground' : 'warning.dark',
                    },
                  }}
                >
                  <BlockOutlinedIcon sx={{ color: user.isDisabled ? 'action.disabled' : 'common.white' }} />
                </IconButton>
              </TableCell>
            </TableRow>
          );
        })}
      </StyledTable>

      <Dialog
        open={Boolean(selectedUser)}
        onClose={handleCloseUserDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Edit user</DialogTitle>
        <Form
          key={selectedUser?.id ?? 'no-user'}
          onSubmit={handleUserSubmit}
          initialValues={initialFormValues}
          validate={editUserValidation}
          render={({ handleSubmit: submitForm }) => (
            <form onSubmit={submitForm} noValidate>
              <DialogContent sx={{ pt: 1 }}>
                <EditUserForm locations={locations} />
              </DialogContent>
              <DialogActions sx={{ px: 3, pb: 3 }}>
                <Button onClick={handleCloseUserDialog} variant="outlined" color="error" disabled={isSubmitting}>
                  Cancel
                </Button>
                <Button type="submit" variant="contained" color="primary" disabled={isSubmitting}>
                  Submit
                </Button>
              </DialogActions>
            </form>
          )}
        />
      </Dialog>

      <Dialog
        open={Boolean(selectedDisableUser)}
        onClose={handleCloseDisableDialog}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Disable user</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            Are you sure you want to disable {selectedDisableUser?.firstName || selectedDisableUser?.email || 'this user'}?
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button onClick={handleCloseDisableDialog} variant="outlined" color="inherit" disabled={isSubmitting}>
            Cancel
          </Button>
          <Button onClick={handleDisableUser} variant="contained" color="warning" disabled={isSubmitting}>
            Disable
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={Boolean(selectedRoleUser)}
        onClose={handleCloseRoleDialog}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Edit role</DialogTitle>
        <Form
          key={selectedRoleUser?.id ?? 'no-role-user'}
          onSubmit={handleRoleSubmit}
          initialValues={initialRoleFormValues}
          validate={editUserRoleValidation}
          render={({ handleSubmit: submitForm }) => (
            <form onSubmit={submitForm} noValidate>
              <DialogContent sx={{ pt: 1 }}>
                <Grid container spacing={2}>
                  <Grid size={{ xs: 12 }}>
                    <FormControlledSelect
                      name="role"
                      label="Role"
                      variant="outlined"
                      data={roleOptions}
                      fullWidth
                    />
                  </Grid>
                </Grid>
              </DialogContent>
              <DialogActions sx={{ px: 3, pb: 3 }}>
                <Button onClick={handleCloseRoleDialog} variant="outlined" color="error" disabled={isSubmitting}>
                  Cancel
                </Button>
                <Button type="submit" variant="contained" color="primary" disabled={isSubmitting}>
                  Submit
                </Button>
              </DialogActions>
            </form>
          )}
        />
      </Dialog>
    </Box>
  );
}